/**
 * Everything this device still holds that has not reached the server, counted
 * from IndexedDB. One list, used by the sync pill (SyncStatusIndicator) and by
 * the MAUI upgrade prompt, which must not send a user to the store while
 * anything is unsent (plan MR1: a stranded queue lost in the update).
 *
 * Loose `!is_synced` filters, NOT the `is_synced` index: several tables write
 * `is_synced: false` (a boolean), which IndexedDB cannot index, so
 * `.where('is_synced').equals(0)` would silently count 0 for those rows. The
 * comments in SyncStatusIndicator record the incidents behind each entry.
 */
import { db } from './db';
import { personnelPositionSyncService } from './personnel-position-sync.service';
import { infrastructureOfflineService } from './infrastructure.service';
import { incidentOfflineService } from './incident-offline.service';

export interface LocalPendingWork {
  pendingMutations: number;
  unsyncedAssessments: number;
  unsyncedPhotos: number;
  unsyncedSupplyRequests: number;
  unsyncedOfflineSupplyRequests: number;
  unsyncedBreadcrumbs: number;
  unsyncedBoardPosts: number;
  unsyncedUnitLogs: number;
  unsyncedActivityEntries: number;
  unsyncedSnapshots: number;
  unsyncedPersonnelPositions: number;
  unsyncedTasks: number;
  unsyncedInfrastructure: number;
  unsyncedFemaCosts: number;
  unsyncedIncidents: number;
  unsyncedForms: number;
  unsyncedCheckins: number;
}

export async function countLocalPendingWork(): Promise<LocalPendingWork> {
  const [
    pendingMutations,
    unsyncedAssessments,
    unsyncedPhotos,
    unsyncedSupplyRequests,
    unsyncedOfflineSupplyRequests,
    unsyncedBreadcrumbs,
    unsyncedBoardPosts,
    unsyncedUnitLogs,
    unsyncedActivityEntries,
    unsyncedSnapshots,
    unsyncedPersonnelPositions,
    unsyncedTasks,
    unsyncedInfrastructure,
    unsyncedFemaCosts,
    unsyncedIncidents,
    unsyncedForms,
    unsyncedCheckins,
  ] = await Promise.all([
    // 'syncing' included: a row stranded by a session that died mid-replay is still unsent work.
    db.pendingMutations.where('status').anyOf(['pending', 'failed', 'syncing']).count(),
    db.cachedDamageAssessments.filter((a) => !a.is_synced).count(),
    db.offlinePhotos.filter((p) => !p.is_synced).count(),
    db.cachedSupplyRequests.filter((r) => !r.is_synced).count(),
    db.offlineSupplyRequests.where('is_synced').equals(0).count(),
    db.offlineBreadcrumbs.filter((b) => !b.is_synced).count(),
    db.cachedBoardPosts.filter((p) => !p.is_synced).count(),
    db.cachedUnitLogs.filter((l) => !l.is_synced).count(),
    db.cachedActivityEntries.filter((e) => !e.is_synced).count(),
    db.offlineSnapshots.filter((s) => !s.is_synced).count(),
    personnelPositionSyncService.getPendingCount(),
    db.cachedTasks.filter((t) => !t.is_synced).count(),
    infrastructureOfflineService.getPendingCount(),
    db.cachedFemaCosts.filter((c) => !c.is_synced).count(),
    incidentOfflineService.getPendingCount(),
    db.offlineForms.filter((f) => !f.is_synced).count(),
    db.offlineCheckins.filter((c) => !c.is_synced).count(),
  ]);
  return {
    pendingMutations,
    unsyncedAssessments,
    unsyncedPhotos,
    unsyncedSupplyRequests,
    unsyncedOfflineSupplyRequests,
    unsyncedBreadcrumbs,
    unsyncedBoardPosts,
    unsyncedUnitLogs,
    unsyncedActivityEntries,
    unsyncedSnapshots,
    unsyncedPersonnelPositions,
    unsyncedTasks,
    unsyncedInfrastructure,
    unsyncedFemaCosts,
    unsyncedIncidents,
    unsyncedForms,
    unsyncedCheckins,
  };
}

export function totalLocalPendingWork(c: LocalPendingWork): number {
  return Object.values(c).reduce((sum, n) => sum + (Number.isFinite(n) && n > 0 ? n : 0), 0);
}
